/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { omit, map, get } from 'lodash';
import { Icon, Message, Form, Input, Label } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Modal, Button, FormLabel } from '../../../../../components';
import ServiceTypeSelect from './ServiceTypeSelect';
import DeleteServiceGroupButton from './DeleteServiceGroupButton';

type EditServiceGroupDialogProps = {
    intl: Object,
    trigger?: any,
    open: boolean,
    onClose: () => void,
    serviceGroup: {
        id: string,
        name: string,
        nameVariants: { lv?: string, ru?: string, en?: string },
        type?: string,
    },
};

const intlMessages = defineMessages({
    serviceGroupNamePlaceholder: {
        id: 'pages.settings.services.groupForm.placeholder.name',
        defaultMessage: 'Group name',
    },
    groupNameError: {
        id: 'pages.settings.services.groupForm.groupNameError',
        defaultMessage: 'Enter group name',
    },
    existingsError: {
        id: 'pages.settings.services.groupForm.existingsError',
        defaultMessage: 'This service does not exist',
    },
    nameExistingsError: {
        id: 'pages.settings.services.groupForm.nameExistingsError',
        defaultMessage: 'Service group with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const UPDATE_SERVICE_GROUP_MUTATION = gql`
    mutation updateServiceGroup(
        $input: UpdateServiceGroupInput!
        $lang: String!
    ) {
        updateServiceGroup(input: $input) {
            serviceGroup {
                id
                name(lang: $lang)
                nameVariants {
                    lv
                    ru
                    en
                }
                type
                services {
                    id
                }
            }
        }
    }
`;

const EditServiceGroupDialog = ({
    trigger,
    open,
    onClose,
    serviceGroup,
    intl,
}: EditServiceGroupDialogProps) => {
    const currentLanguage = useSelector(
        ({ intl: intlState }) =>
            intlState.get('locale') || intlState.get('defaultLanguage'),
    );
    const languages = useSelector(({ intl: intlState }) =>
        intlState.get('languages'),
    );
    const defaultLanguage = useSelector(({ intl: intlState }) =>
        intlState.get('defaultLanguage'),
    );

    const buildDefaultValues = () => ({
        nameVariants: serviceGroup
            ? serviceGroup.nameVariants
            : (languages || []).reduce((acc, lang) => ({ ...acc, [lang]: '' }), {}),
        type: serviceGroup ? serviceGroup.type : null,
    });

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({
        defaultValues: buildDefaultValues(),
    });

    useEffect(() => {
        if (open && serviceGroup) {
            reset({
                nameVariants: serviceGroup.nameVariants,
                type: serviceGroup.type,
            });
        }
    }, [open, serviceGroup, reset]);

    const [updateServiceGroup] = useMutation(UPDATE_SERVICE_GROUP_MUTATION);

    const onSubmit = async formData => {
        try {
            await updateServiceGroup({
                variables: {
                    input: {
                        serviceGroupId: serviceGroup.id,
                        nameVariants: omit(formData.nameVariants, ['__typename']),
                        type: formData.type || null,
                    },
                    lang: currentLanguage,
                },
            });
            onClose();
        } catch (error) {
            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'NotExists' &&
                    get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.existingsError),
                    });
                    return;
                } else if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal size="small" trigger={trigger} open={open} onClose={onClose}>
            <Modal.Header>
                <FormattedMessage
                    id="pages.settings.services.groupForm.editServiceGroup"
                    defaultMessage="Edit Service Group {name}"
                    values={{ name: serviceGroup ? serviceGroup.name : '' }}
                />
            </Modal.Header>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.settings.services.serviceForm.name"
                            defaultMessage="Name"
                        />
                    </FormLabel>
                    <Form.Group widths="equal">
                        {map(languages, lang => (
                            <Form.Field
                                key={lang}
                                error={
                                    !!(
                                        errors.nameVariants &&
                                        errors.nameVariants[lang]
                                    )
                                }
                            >
                                <FormLabel>{lang.toUpperCase()}</FormLabel>
                                <Controller
                                    name={`nameVariants.${lang}`}
                                    control={control}
                                    rules={
                                        lang === defaultLanguage
                                            ? {
                                                  required: intl.formatMessage(
                                                      intlMessages.groupNameError,
                                                  ),
                                              }
                                            : {}
                                    }
                                    render={({ field: { ref: _ref, ...field } }) => (
                                        <Input
                                            {...field}
                                            placeholder={intl.formatMessage(
                                                intlMessages.serviceGroupNamePlaceholder,
                                            )}
                                            fluid
                                        />
                                    )}
                                />
                                {errors.nameVariants &&
                                    errors.nameVariants[lang] && (
                                        <Label color="red" pointing basic>
                                            {errors.nameVariants[lang].message}
                                        </Label>
                                    )}
                            </Form.Field>
                        ))}
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <FormattedMessage
                                id="pages.settings.services.groupForm.placeholder.type"
                                defaultMessage="Group type"
                            >
                                {placeholder => (
                                    <Controller
                                        name="type"
                                        control={control}
                                        render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                            <ServiceTypeSelect
                                                {...field}
                                                value={value || undefined}
                                                onChange={(e, data) =>
                                                    onChange(data.value)
                                                }
                                                placeholder={placeholder}
                                            />
                                        )}
                                    />
                                )}
                            </FormattedMessage>
                        </Form.Field>
                    </Form.Group>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    primary
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.settings.services.groupForm.buttons.save"
                        defaultMessage="Save"
                    />
                </Button>
                <Button disabled={isSubmitting} onClick={onClose} basic>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.settings.services.groupForm.buttons.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>

                <DeleteServiceGroupButton serviceGroup={serviceGroup} />
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(EditServiceGroupDialog);
